import {
  type GlobalRolePermissions,
  type MspPortalPermissions,
  type MspSpecialPermissions,
  type NestedGlobalPermissions,
  type NestedMspPermissions,
} from "@/_store/roles.module";
import { RolePermissionAccessMode } from "@/constants/roles";
import { toRaw } from "vue";
import { isGlobalAccessRestricted, isGlobalActionRestricted } from "@/_helpers/global-permissions";
import get from "lodash/get";
import { usePermissionsStore } from "@/_store/permissions.module.ts";

export function isMspActionRestricted<
  T extends keyof MspPortalPermissions,
  K extends keyof MspPortalPermissions[T],
  V extends MspPortalPermissions[T][K] extends NestedMspPermissions
    ? NonNullable<MspPortalPermissions[T][K]["editAccessModePermission"]>
    : MspPortalPermissions[T][K],
>(scopeName: T, key: NonNullable<K>, nestedPermissionKey?: keyof V) {
  const permissions = getMspPermissionsByScopeNameAndKey(scopeName, key);
  // Filter case when permissions doesn't exist
  if (!permissions) {
    return isGlobalActionRestricted(
      scopeName,
      key,
      nestedPermissionKey as keyof (GlobalRolePermissions[T][K] extends NestedGlobalPermissions
        ? NonNullable<GlobalRolePermissions[T][K]["editAccessModePermission"]>
        : GlobalRolePermissions[T][K])
    );
  }
  const isSpecialPermissions = (
    permission: RolePermissionAccessMode | NestedMspPermissions | MspSpecialPermissions
  ): permission is MspSpecialPermissions => {
    if (typeof permissions !== "object") return false;
    return Object.values(permission).every((val) => typeof val === "boolean");
  };

  // Filter case when permissions is just string
  if (typeof permissions === "string") {
    return permissions !== RolePermissionAccessMode.EDIT;
  }

  if (isSpecialPermissions(permissions)) {
    return !permissions[nestedPermissionKey as keyof MspSpecialPermissions];
  }

  // Filter case when permissions is an object of such shape
  // {
  //   accessMode: RolePermissionAccessMode;
  //   editAccessModePermission?: {[key: string]: boolean}
  // };

  if (typeof permissions === "object") {
    // If no key provided, then check if it's edit or not
    if (!nestedPermissionKey) {
      return permissions.accessMode !== RolePermissionAccessMode.EDIT;
    }

    // If key is provided, then check the particular action
    if (
      nestedPermissionKey &&
      permissions.accessMode === RolePermissionAccessMode.EDIT &&
      permissions.editAccessModePermission
    ) {
      return permissions.editAccessModePermission
        ? // @ts-ignore
          !permissions.editAccessModePermission[nestedPermissionKey]
        : true;
    }
  }

  return true;
}

export function isMspAccessRestricted<
  T extends keyof MspPortalPermissions = keyof MspPortalPermissions,
  K extends keyof MspPortalPermissions[T] = keyof MspPortalPermissions[T],
>(scopeName: T, key: K) {
  const permissions = getMspPermissionsByScopeNameAndKey(scopeName, key);
  if (!permissions) return isGlobalAccessRestricted(scopeName, key);

  if (typeof permissions === "string") {
    return permissions === RolePermissionAccessMode.NO_ACCESS;
  }
  return (permissions as NestedMspPermissions).accessMode
    ? (permissions as NestedMspPermissions).accessMode === RolePermissionAccessMode.NO_ACCESS
    : true;
}

function getMspPermissionsByScopeNameAndKey<
  T extends keyof MspPortalPermissions = keyof MspPortalPermissions,
  K extends keyof MspPortalPermissions[T] = keyof MspPortalPermissions[T],
>(scopeName: T, key: K) {
  const permissionsStore = usePermissionsStore();
  const mspPermissions: MspPortalPermissions | null = toRaw(permissionsStore.mspPortalPermissions);
  return get(mspPermissions, [scopeName, key]) as
    | NestedMspPermissions
    | MspSpecialPermissions
    | RolePermissionAccessMode
    | undefined;
}
